import type { Article } from '../types/Article';

export interface MetaConfig {
  title: string;
  description: string;
  url: string;
  image?: string;
  type?: 'website' | 'article';
  robots?: string;
  canonical?: string;
  publishedTime?: string | null;
  modifiedTime?: string | null;
  structuredData?: Record<string, unknown> | Record<string, unknown>[];
}

const MONTHS_FR: Record<string, string> = {
  janvier: '01',
  fevrier: '02',
  février: '02',
  mars: '03',
  avril: '04',
  mai: '05',
  juin: '06',
  juillet: '07',
  aout: '08',
  août: '08',
  septembre: '09',
  octobre: '10',
  novembre: '11',
  decembre: '12',
  décembre: '12',
};

export class SEOManager {
  private siteName = 'World Of Numeric';
  private siteUrl = 'https://worldofnumeric.fr';
  private defaultImage = `${this.siteUrl}/og/article-default.webp`;
  private structuredDataScriptId = 'won-structured-data';

  updateMetaTags(config: MetaConfig): void {
    if (!config) return;
    const type = config.type || 'website';
    const image = this.ensureAbsoluteUrl(config.image || this.defaultImage);
    const canonical = this.ensureAbsoluteUrl(config.canonical || config.url);

    document.title = config.title;
    this.setMeta('description', config.description);
    this.setMeta('robots', config.robots || 'index,follow');
    this.setLink('canonical', canonical);

    this.setProperty('og:type', type);
    this.setProperty('og:title', config.title);
    this.setProperty('og:description', config.description);
    this.setProperty('og:url', this.ensureAbsoluteUrl(config.url));
    this.setProperty('og:site_name', this.siteName);
    this.setProperty('og:image', image);

    if (type === 'article') {
      if (config.publishedTime) {
        this.setProperty('article:published_time', config.publishedTime);
      }
      if (config.modifiedTime) {
        this.setProperty('article:modified_time', config.modifiedTime);
      }
    } else {
      this.removeProperty('article:published_time');
      this.removeProperty('article:modified_time');
    }

    this.setMeta('twitter:card', 'summary_large_image');
    this.setMeta('twitter:title', config.title);
    this.setMeta('twitter:description', config.description);
    this.setMeta('twitter:image', image);

    if (config.structuredData) {
      this.injectStructuredData(config.structuredData);
    }
  }

  getBlogPageSEO(): MetaConfig {
    return {
      title: 'Blog IA et Transformation Digitale - World Of Numeric',
      description:
        "Articles, interviews et retours d'expérience sur l'intelligence artificielle, la transformation digitale et la gouvernance des données.",
      url: `${this.siteUrl}/blog.html`,
      image: `${this.siteUrl}/og/blog-hero.webp`,
      type: 'website',
      structuredData: {
        '@context': 'https://schema.org',
        '@type': 'Blog',
        name: 'Blog World Of Numeric',
        url: `${this.siteUrl}/blog.html`,
        description:
          "Articles, interviews et retours d'expérience sur l'intelligence artificielle et la transformation digitale.",
        publisher: {
          '@type': 'Organization',
          name: this.siteName,
          logo: {
            '@type': 'ImageObject',
            url: `${this.siteUrl}/icons/icon-512.png`,
          },
        },
      },
    };
  }

  getContactPageSEO(): MetaConfig {
    return {
      title: 'Contact - World Of Numeric',
      description: 'Prenez rendez-vous avec World Of Numeric pour accélérer votre transformation digitale et IA.',
      url: `${this.siteUrl}/contact.html`,
      image: `${this.siteUrl}/og/home-hero.webp`,
      type: 'website',
      structuredData: {
        '@context': 'https://schema.org',
        '@type': 'ContactPage',
        url: `${this.siteUrl}/contact.html`,
        mainEntity: {
          '@type': 'Organization',
          name: this.siteName,
          email: 'contact@worldofnumeric.fr',
        },
      },
    };
  }

  getArticleSEO(article: Article, slug: string): MetaConfig {
    const articleUrl = `${this.siteUrl}/single-article.html?slug=${slug}`;
    const image = this.ensureAbsoluteUrl(article.image || this.defaultImage);
    const published = this.parseDate(article.date);
    const updated = this.parseDate(article.metadata?.lastUpdated) || published;

    const description = article.excerpt || '';
    const authorName = article.metadata?.author || article.author;

    return {
      title: `${article.title} - ${this.siteName}`,
      description,
      url: articleUrl,
      image,
      type: 'article',
      canonical: articleUrl,
      structuredData: {
        '@context': 'https://schema.org',
        '@type': 'Article',
        headline: article.title,
        description: article.excerpt,
        image,
        datePublished: published || undefined,
        dateModified: updated || undefined,
        author: authorName
          ? {
              '@type': 'Person',
              name: authorName,
            }
          : {
              '@type': 'Organization',
              name: this.siteName,
            },
        publisher: {
          '@type': 'Organization',
          name: this.siteName,
          logo: {
            '@type': 'ImageObject',
            url: `${this.siteUrl}/icons/icon-512.png`,
          },
        },
        mainEntityOfPage: {
          '@type': 'WebPage',
          '@id': articleUrl,
        },
      },
      publishedTime: published,
      modifiedTime: updated,
    };
  }

  removeStructuredData(): void {
    const existing = document.getElementById(this.structuredDataScriptId);
    if (existing && existing.parentElement) {
      existing.parentElement.removeChild(existing);
    }
  }

  private injectStructuredData(data: Record<string, unknown> | Record<string, unknown>[]): void {
    this.removeStructuredData();
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = this.structuredDataScriptId;
    script.textContent = JSON.stringify(data);
    document.head.appendChild(script);
  }

  private setMeta(name: string, content: string): void {
    if (!name || !content) return;
    let tag = document.querySelector(`meta[name="${name}"]`) as HTMLMetaElement | null;
    if (!tag) {
      tag = document.createElement('meta');
      tag.name = name;
      document.head.appendChild(tag);
    }
    tag.content = content;
  }

  private setProperty(property: string, content: string): void {
    if (!property || !content) return;
    let tag = document.querySelector(`meta[property="${property}"]`) as HTMLMetaElement | null;
    if (!tag) {
      tag = document.createElement('meta');
      tag.setAttribute('property', property);
      document.head.appendChild(tag);
    }
    tag.content = content;
  }

  private removeProperty(property: string): void {
    const tag = document.querySelector(`meta[property="${property}"]`);
    if (tag && tag.parentElement) {
      tag.parentElement.removeChild(tag);
    }
  }

  private setLink(rel: string, href: string): void {
    if (!rel || !href) return;
    let link = document.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement | null;
    if (!link) {
      link = document.createElement('link');
      link.rel = rel;
      document.head.appendChild(link);
    }
    link.href = href;
  }

  private ensureAbsoluteUrl(input?: string | null): string {
    if (!input) return this.defaultImage;
    if (/^https?:/i.test(input)) {
      return input;
    }
    if (input.startsWith('//')) {
      return `https:${input}`;
    }
    if (!input.startsWith('/')) {
      return `${this.siteUrl}/${input}`.replace(/([^:]\/)(\/)+/g, '$1/');
    }
    return `${this.siteUrl}${input}`;
  }

  private parseDate(value?: string | null): string | null {
    if (!value) return null;
    const match = value
      .toLowerCase()
      .match(/(\d{1,2})\s+([a-zéûîôêàèùï]+)\s+(\d{4})/i);
    if (!match) return null;
    const [, day, monthNameRaw, year] = match;
    const month = MONTHS_FR[monthNameRaw];
    if (!month) return null;
    const formattedDay = day.padStart(2, '0');
    return `${year}-${month}-${formattedDay}`;
  }
}

export default SEOManager;
